import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItemButton,
  ListItemIcon,
  Avatar,
  ListItemText,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  PeopleAlt as PeopleAltIcon,
  Business as BusinessIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  TaskAlt as TaskAltIcon,
  Whatshot as WhatshotIcon,
} from "@mui/icons-material";
import GoogleIcon from "@mui/icons-material/Google"; // Gmail
import WindowIcon from "@mui/icons-material/Window"; // Outlook
import { motion } from "framer-motion";
import {
  formatTimestamp,
  hasValidEmail,
  hasValidPhone,
  getTravelPriority,
} from "./ConsultantUtils";

const MotionTableRow = motion(TableRow);

/* -----------------------------
   Tracked URL builder (local)
   - avoids relying on getTrackedLink('email', ...) which was returning '#' / not opening
------------------------------ */
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

function normalizePhoneIntl(v) {
  if (!v) return "";
  let s = String(v).replace(/[^\d+]/g, "");
  if (!s.startsWith("+")) {
    if (s.startsWith("0")) s = `+263${s.slice(1)}`;
    else if (s.startsWith("263")) s = `+${s}`;
    else if (/^\d{7,15}$/.test(s)) s = `+${s}`;
  }
  if (!/^\+\d{7,15}$/.test(s)) return "";
  return s;
}

function buildTrackedUrl(channel, params = {}) {
  const { source } = params;
  if (!channel || !source) return undefined;

  let to = params.to ?? "";
  let target = params.target || "";

  if (channel === "email" || channel === "gmail" || channel === "outlook" || channel === "yahoo") {
    if (!hasValidEmail(to)) return undefined;
    to = String(to).trim();
  } else if (channel === "tel" || channel === "whatsapp") {
    const p = normalizePhoneIntl(to);
    if (!p) return undefined;
    to = p;
  } else if (channel === "calendar") {
    if (!target) return undefined;
  } else {
    return undefined;
  }

  const qs = new URLSearchParams({
    channel,
    to,
    source: String(source),
  });

  if (params.contact_id) qs.set("contact_id", String(params.contact_id));
  if (params.contact_name) qs.set("name", String(params.contact_name));
  if (params.subject) qs.set("subject", String(params.subject));
  if (params.body) qs.set("body", String(params.body));
  if (params.text) qs.set("text", String(params.text));
  if (target) qs.set("target", String(target));

  return `${API_BASE}/api/t?${qs.toString()}`;
}

function openTracked(link) {
  if (!link) return;
  window.open(link, "_blank", "noopener,noreferrer");
}

/* =============================
   MyInquiriesList (UPDATED EMAIL)
============================= */
export const MyInquiriesList = ({ inquiries, loading, user, onRowClick }) => {
  // Email menu state
  const [emailAnchor, setEmailAnchor] = useState(null);
  const [emailCtx, setEmailCtx] = useState(null);

  const closeEmailMenu = () => {
    setEmailAnchor(null);
    setEmailCtx(null);
  };

  const openEmailMenu = (e, inq) => {
    e.preventDefault();
    e.stopPropagation();

    const contactEmail = inq?.formData?.email || "";
    if (!hasValidEmail(contactEmail)) return;

    const customerName = inq?.formData?.fullName || "Customer";
    const serviceType = inq?.type || "Inquiry";

    const subjectLine = `Regarding: ${serviceType}`;
    const bodyContent =
      `Hi ${customerName},\n\n` +
      `Thank you for your inquiry regarding ${serviceType}.\n\n` +
      `How can we help you further?\n\n` +
      `Best regards,\nAbisiniya Team`;

    // best contact_id for tracking: sqlId > firestore id > email
    const contactId = inq?.sqlId || inq?.id || contactEmail;

    setEmailCtx({
      to: contactEmail,
      contact_id: contactId,
      contact_name: customerName,
      subject: subjectLine,
      body: bodyContent,
      source: "consultant_inquiries",
    });
    setEmailAnchor(e.currentTarget);
  };

  const selectEmailProvider = (provider) => {
    if (!emailCtx) return;
    const link = buildTrackedUrl(provider, emailCtx);
    openTracked(link);
    closeEmailMenu();
  };

  return (
    <motion.div
      key="inquiries-list"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ height: "100%" }}
    >
      <Paper
        sx={{
          p: { xs: 1, sm: 2 },
          borderRadius: "12px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{ mb: 2, fontWeight: 700, flexShrink: 0 }}
        >
          My Inquiries ({inquiries.length})
        </Typography>

        {loading && inquiries.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : inquiries.length === 0 ? (
          <Typography sx={{ textAlign: "center", p: 2, fontStyle: "italic" }}>
            No inquiries available.
          </Typography>
        ) : (
          <TableContainer sx={{ flexGrow: 1, overflowY: "auto", overflowX: "auto" }}>
            <Table stickyHeader size="small" sx={{ minWidth: 360 }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: "bold",
                      bgcolor: "background.paper",
                    },
                  }}
                >
                  <TableCell>Customer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    Last Update
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    Assigned To
                  </TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {inquiries.map((inq) => {
                  const contactEmail = inq.formData?.email;
                  const contactPhone = inq.formData?.phone;

                  const hasPhone = hasValidPhone(contactPhone);
                  const hasEmail = hasValidEmail(contactEmail);

                  const telHref = hasPhone
                    ? buildTrackedUrl("tel", {
                        to: contactPhone,
                        source: "consultant_inquiries",
                        contact_id: inq?.sqlId || inq?.id || contactPhone,
                        contact_name: inq.formData?.fullName || "Customer",
                      })
                    : undefined;

                  const waHref = hasPhone
                    ? buildTrackedUrl("whatsapp", {
                        to: contactPhone,
                        source: "consultant_inquiries",
                        contact_id: inq?.sqlId || inq?.id || contactPhone,
                        contact_name: inq.formData?.fullName || "Customer",
                        text: "Hello! Can we chat about your inquiry?",
                      })
                    : undefined;

                  const isClosed = [
                    "closed_by_consultant",
                    "closed_by_user",
                    "closed_by_provider",
                  ].includes(inq.status);

                  const priorityData = getTravelPriority(inq.formData?.travelDate);
                  const isUrgentRow =
                    !isClosed && ["urgent", "overdue"].includes(priorityData.level);
                  const isHotRow = !isClosed && priorityData.level === "hot";

                  const isUnreadOrNewForMe =
                    !isClosed &&
                    ((String(inq.consultantId) === String(user?.uid) &&
                      (inq.consultantUnreadCount > 0 ||
                        inq.status === "assigned")) ||
                      ((!inq.consultantId || inq.consultantId === "") &&
                        ["new_chat", "pending_consultant", "assigned"].includes(
                          inq.status
                        )));

                  return (
                    <MotionTableRow
                      key={inq.id}
                      hover
                      onClick={() => onRowClick(inq)}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      sx={{
                        cursor: "pointer",
                        bgcolor: isUnreadOrNewForMe
                          ? "action.selected"
                          : isUrgentRow
                          ? "rgba(244,67,54,0.04)"
                          : "inherit",
                        borderLeft: isUrgentRow
                          ? "4px solid #f44336"
                          : isHotRow
                          ? "4px solid #ff9800"
                          : undefined,
                        "&:hover": { bgcolor: "action.hover" },
                        "&:hover .quick-actions-table": {
                          opacity: 1,
                          transform: "translateY(-50%) scale(1)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: isUnreadOrNewForMe ? 600 : 500,
                          position: "relative",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {isUrgentRow && (
                            <Typography title="Urgent" sx={{ fontSize: "1.2rem" }}>
                              ⚡
                            </Typography>
                          )}

                          {isHotRow && (
                            <Tooltip title="Hot Lead">
                              <WhatshotIcon
                                sx={{
                                  fontSize: "1.2rem",
                                  color: "#ffd600",
                                  animation: "flashYellow 1.2s infinite",
                                }}
                              />
                            </Tooltip>
                          )}

                          {inq.formData?.fullName || "Unknown"}

                          {inq.inquiryUserType === "service_provider_v2" && (
                            <Chip
                              label="Provider"
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                color: "success.dark",
                                borderColor: "success.dark",
                                pointerEvents: "none",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </Box>

                        {/* Quick Actions (Green Pill) */}
                        <Box
                          className="quick-actions-table"
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            position: "absolute",
                            left: "60%",
                            top: "50%",
                            transform: "translateY(-50%) scale(0.8)",
                            bgcolor: "success.main",
                            borderRadius: 999,
                            px: 1,
                            py: 0.5,
                            display: "flex",
                            gap: 0.5,
                            boxShadow: 3,
                            opacity: 0,
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            zIndex: 10,
                          }}
                        >
                          {/* CALL */}
                          <Tooltip title="Call">
                            <span>
                              <IconButton
                                component="a"
                                href={telHref}
                                disabled={!hasPhone}
                                size="small"
                                sx={{ color: "white" }}
                                onClick={(e) => {
                                  // allow the anchor to navigate, just stop row click
                                  e.stopPropagation();
                                }}
                              >
                                <PhoneIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* WHATSAPP */}
                          <Tooltip title="WhatsApp">
                            <span>
                              <IconButton
                                component="a"
                                href={waHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={!hasPhone}
                                size="small"
                                sx={{ color: "white" }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* EMAIL (UPDATED: menu + window.open) */}
                          <Tooltip title={hasEmail ? "Email" : "No email"}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={!hasEmail}
                                sx={{ color: "white" }}
                                onClick={(e) => openEmailMenu(e, inq)}
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={inq.status?.replace(/_/g, " ") || "Pending"}
                          size="small"
                          color={isClosed ? "default" : isUrgentRow ? "error" : "info"}
                          variant={isClosed ? "outlined" : "filled"}
                          sx={{
                            textTransform: "capitalize",
                            opacity: isClosed ? 0.7 : 1,
                            pointerEvents: "none",
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                        {formatTimestamp(inq.updatedAt)}
                      </TableCell>

                      <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                        {inq.type || "N/A"}
                      </TableCell>

                      <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                        {inq.consultantName || "Pool"}
                      </TableCell>

                      <TableCell align="right">
                        <Button variant="outlined" size="small" sx={{ borderRadius: "6px" }}>
                          Open
                        </Button>
                      </TableCell>
                    </MotionTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Email Provider Menu */}
        <Menu
          anchorEl={emailAnchor}
          open={Boolean(emailAnchor)}
          onClose={closeEmailMenu}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{ elevation: 3, sx: { borderRadius: 2, mt: 1, minWidth: 180 } }}
        >
          <MenuItem onClick={() => selectEmailProvider("gmail")}>
            <ListItemIcon>
              <GoogleIcon fontSize="small" sx={{ color: "#DB4437" }} />
            </ListItemIcon>
            <ListItemText>Gmail</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => selectEmailProvider("outlook")}>
            <ListItemIcon>
              <WindowIcon fontSize="small" sx={{ color: "#0078D4" }} />
            </ListItemIcon>
            <ListItemText>Outlook Web</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => selectEmailProvider("yahoo")}>
            <ListItemIcon>
              <EmailIcon fontSize="small" sx={{ color: "#6001D2" }} />
            </ListItemIcon>
            <ListItemText>Yahoo Mail</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => selectEmailProvider("email")}>
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Default App</ListItemText>
          </MenuItem>
        </Menu>

        {/* local animation for hot lead icon */}
        <style>
          {`
            @keyframes flashYellow {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.2; }
            }
          `}
        </style>
      </Paper>
    </motion.div>
  );
};

/* =============================
   MyTasksList (UNCHANGED)
============================= */
export const MyTasksList = ({ tasks, loading, onMarkComplete }) => {
  return (
    <motion.div
      key="tasks-list"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ height: "100%" }}
    >
      <Paper
        sx={{
          p: { xs: 1, sm: 2 },
          borderRadius: "12px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{ mb: 2, fontWeight: 700, flexShrink: 0 }}
        >
          My Tasks ({tasks.filter((t) => t.status !== "completed").length})
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : tasks.length === 0 ? (
          <Typography sx={{ textAlign: "center", p: 2, fontStyle: "italic" }}>
            No tasks assigned.
          </Typography>
        ) : (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <List>
              {tasks.map((task) => {
                const assignedByLabel =
                  task.assignedByName ||
                  task.createdByName ||
                  task.createdBy ||
                  task.assignedBy ||
                  "";

                const secondaryParts = [];
                secondaryParts.push(
                  `Assigned: ${formatTimestamp(task.assignedAt || task.createdAt)}`
                );
                if (assignedByLabel) secondaryParts.push(`By ${assignedByLabel}`);

                const secondaryLine = secondaryParts.join(" · ");

                return (
                  <ListItemButton
                    key={task.id}
                    sx={{
                      borderRadius: "8px",
                      mb: 1,
                      border: 1,
                      borderColor: task.status === "completed" ? "divider" : "primary.main",
                      bgcolor: task.status === "completed" ? "#fafafa" : "transparent",
                      opacity: task.status === "completed" ? 0.7 : 1,
                    }}
                  >
                    <ListItemIcon>
                      <AssignmentTurnedInIcon
                        color={task.status === "completed" ? "success" : "primary"}
                      />
                    </ListItemIcon>

                    <ListItemText
                      primary={task.taskDescription || "No Description"}
                      secondary={secondaryLine}
                      primaryTypographyProps={{
                        textDecoration: task.status === "completed" ? "line-through" : "none",
                      }}
                    />

                    {task.status !== "completed" && (
                      <Tooltip title="Mark as Done">
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMarkComplete(task);
                          }}
                          color="success"
                        >
                          <TaskAltIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        )}
      </Paper>
    </motion.div>
  );
};

/* =============================
   CustomersList (OPTIONAL EMAIL FIX TOO)
   - same idea: email icon opens provider menu (kept simple: Default App only)
============================= */
export const CustomersList = ({ customers, loading, onCustomerView }) => {
  // Optional: keep it simple (default email only). If you want provider menu here too, say so.
  return (
    <motion.div
      key="customers-list"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ height: "100%" }}
    >
      <Paper
        sx={{
          p: { xs: 1, sm: 2 },
          borderRadius: "12px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{ mb: 2, fontWeight: 700, flexShrink: 0 }}
        >
          Customers ({customers.length})
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : customers.length === 0 ? (
          <Typography sx={{ textAlign: "center", p: 2, fontStyle: "italic" }}>
            No customer history.
          </Typography>
        ) : (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <List>
              {customers.map((cust) => {
                const hasPhone = hasValidPhone(cust.phone);
                const hasEmail = hasValidEmail(cust.email);

                const telHref = hasPhone
                  ? buildTrackedUrl("tel", {
                      to: cust.phone,
                      source: "consultant_customers",
                      contact_id: cust.id || cust.phone,
                      contact_name: cust.fullName || "Customer",
                    })
                  : undefined;

                const waHref = hasPhone
                  ? buildTrackedUrl("whatsapp", {
                      to: cust.phone,
                      source: "consultant_customers",
                      contact_id: cust.id || cust.phone,
                      contact_name: cust.fullName || "Customer",
                      text: "Hello! Can we chat about your inquiry?",
                    })
                  : undefined;

                const emailLink = hasEmail
                  ? buildTrackedUrl("email", {
                      to: cust.email,
                      source: "consultant_customers",
                      contact_id: cust.id || cust.email,
                      contact_name: cust.fullName || "Customer",
                      subject: "Following up",
                      body: `Hi ${cust.fullName || "Customer"},\n\nJust following up.\n\nBest regards,\nAbisiniya Team`,
                    })
                  : undefined;

                return (
                  <ListItemButton
                    key={cust.id}
                    divider
                    onClick={() => onCustomerView?.(cust)}
                    sx={{
                      alignItems: "flex-start",
                      py: 1.5,
                      position: "relative",
                      "&:hover .quick-actions": { opacity: 1, transform: "translateY(0)" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48, mt: 0.5 }}>
                      <Avatar
                        sx={{
                          bgcolor: cust.isServiceProvider ? "secondary.main" : "info.light",
                        }}
                      >
                        {cust.isServiceProvider ? (
                          <BusinessIcon fontSize="small" />
                        ) : (
                          cust.fullName?.charAt(0).toUpperCase() || <PeopleAltIcon />
                        )}
                      </Avatar>
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography component="span" sx={{ fontWeight: 500 }}>
                            {cust.fullName || "N/A"}
                          </Typography>
                          {cust.isServiceProvider && (
                            <Chip
                              label="Provider"
                              size="small"
                              variant="outlined"
                              sx={{
                                height: "auto",
                                lineHeight: "1.2rem",
                                py: 0.2,
                                color: "success.dark",
                                borderColor: "success.dark",
                                pointerEvents: "none",
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          {`${cust.email || ""} | ${cust.phone || ""}`}
                          <br />
                          <Typography component="span" variant="caption">
                            Last Interaction: {formatTimestamp(cust.lastInteraction)}
                          </Typography>
                        </>
                      }
                      secondaryTypographyProps={{ component: "div" }}
                    />

                    <Box
                      className="quick-actions"
                      sx={{
                        position: "absolute",
                        right: 16,
                        top: "50%",
                        marginTop: "-18px",
                        bgcolor: "success.main",
                        borderRadius: 999,
                        px: 1,
                        py: 0.5,
                        display: "flex",
                        gap: 0.5,
                        boxShadow: 3,
                        opacity: 0,
                        transform: "translateY(4px)",
                        transition: "all 0.2s ease",
                        zIndex: 5,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Call">
                        <span>
                          <IconButton
                            component="a"
                            href={telHref}
                            disabled={!hasPhone}
                            size="small"
                            sx={{
                              color: "white",
                              "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
                            }}
                          >
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="WhatsApp">
                        <span>
                          <IconButton
                            component="a"
                            href={waHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={!hasPhone}
                            size="small"
                            sx={{
                              color: "white",
                              "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
                            }}
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Email">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!hasEmail}
                            sx={{
                              color: "white",
                              "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openTracked(emailLink);
                            }}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        )}
      </Paper>
    </motion.div>
  );
};
